import { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { CAPABILITIES } from "@/data/content";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";

export default function CapabilitiesPage() {
  const { slug } = useParams();
  const cap = CAPABILITIES.find((c) => c.slug === slug);
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: cap ? `${cap.name} — BLEZZINGS` : "Capabilities — BLEZZINGS",
    description: cap ? cap.metaDescription : "BLEZZINGS Growth Partners capabilities.",
    canonical: `https://blezzings.co/capabilities/${slug}`,
    breadcrumbs: cap
      ? [
          { name: "Home", url: "https://blezzings.co/" },
          { name: "Our Services", url: `https://blezzings.co/capabilities/${slug}` },
          { name: cap.name, url: `https://blezzings.co/capabilities/${slug}` },
        ]
      : undefined,
    service: cap
      ? {
          name: cap.name,
          description: cap.metaDescription,
          url: `https://blezzings.co/capabilities/${slug}`,
        }
      : undefined,
  });

  if (!cap) return <Navigate to="/" replace />;

  const openBook = (source) => {
    track("book_consultation_open", { source });
    setBookOpen(true);
  };

  return (
    <div data-testid="capability-page" className="relative">
      <Nav onBook={() => openBook("nav")} />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-14 md:pb-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
          <SectionBadge label="Capabilities" />
          <h1
            data-testid="capability-title"
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-4xl"
          >
            {cap.name}
          </h1>
          <p
            data-testid="capability-subhead"
            className="mt-6 text-xl md:text-2xl text-zinc-700 leading-relaxed max-w-3xl"
          >
            {cap.subhead}
          </p>
          <div
            data-testid="capability-tagline"
            className="mt-5 text-[11px] font-semibold tracking-[0.28em] uppercase text-black"
          >
            {cap.tagline}
          </div>
          <div className="mt-8 max-w-3xl space-y-5 text-zinc-700 leading-relaxed">
            {cap.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Hero image */}
      {cap.image && (
        <section className="pb-16 md:pb-20 bg-[#fafafa]">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
            <div className="relative aspect-[16/10] md:aspect-[21/9] w-full overflow-hidden bg-zinc-200 border border-zinc-300">
              <img
                data-testid="capability-image"
                src={cap.image}
                alt={cap.imageAlt || cap.name}
                className="absolute inset-0 w-full h-full object-cover slow-scale"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
          </div>
        </section>
      )}

      {/* What We Offer */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
          <SectionBadge label="What We Offer" />
          <ul
            data-testid="capability-offers"
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
          >
            {cap.offers.map((offer, i) => (
              <li
                key={offer.title}
                data-testid={`capability-offer-${i}`}
                className="group relative bg-white border border-zinc-200 p-7 md:p-8 flex flex-col transition-all duration-500 hover:border-black hover:-translate-y-1"
              >
                <div className="flex items-center gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-black text-white text-[13px] font-semibold tracking-wider">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif-display text-xl md:text-[22px] leading-snug text-black">
                    {offer.title}
                  </h3>
                </div>
                <p className="mt-5 text-[15px] text-zinc-600 leading-relaxed">
                  {offer.body}
                </p>
                <span
                  aria-hidden
                  className="absolute left-0 bottom-0 h-[3px] w-0 bg-black transition-all duration-500 group-hover:w-full"
                />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Contextual band image */}
      {cap.bandImage && (
        <section className="pb-16 md:pb-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
            <div className="relative aspect-[16/10] md:aspect-[21/9] w-full overflow-hidden bg-zinc-200 border border-zinc-300">
              <img
                data-testid="capability-band-image"
                src={cap.bandImage}
                alt={cap.bandImageAlt || cap.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover slow-scale"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
          </div>
        </section>
      )}

      {/* Who We Serve + Why BLEZZINGS */}
      <section className="py-16 md:py-20 bg-[#F2F2F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          <div>
            <SectionBadge label="Who We Serve" />
            <p
              data-testid="capability-who"
              className="mt-6 text-zinc-700 leading-relaxed"
            >
              {cap.whoWeServe}
            </p>
          </div>
          <div>
            <SectionBadge label="Why BLEZZINGS" />
            <div
              data-testid="capability-why"
              className="mt-6 space-y-5 text-zinc-700 leading-relaxed"
            >
              {cap.whyBlezzings.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-[#050505] text-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
          <p
            data-testid="capability-closing"
            className="font-serif-display text-3xl sm:text-4xl leading-snug"
          >
            {cap.closing}
          </p>
          <p
            data-testid="capability-cta-lead"
            className="mt-5 text-white/70 leading-relaxed"
          >
            {cap.ctaLead}
          </p>
          <button
            data-testid="capability-book-btn"
            onClick={() => openBook("capability-page")}
            className="group mt-8 inline-flex items-center gap-3 h-12 px-7 bg-white text-black text-[15px] font-medium hover:bg-zinc-200 transition-colors duration-300"
          >
            Book a Consultation
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>
      </section>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
